/*
  La vignette d'un stage : la preparer dans le navigateur, l'envoyer dans le
  seau `stages`, la retirer quand plus rien n'y renvoie.

  Le seau porte les memes regles cote serveur (5 Mo, JPG/PNG/WebP, ecriture
  reservee aux admins) : migrations 20260925114714 et 20260925120447.
*/
import { supabase } from '../supabase';
import { optimizeImageIfNeeded } from '../../utils/imageUtils';

const SEAU = 'stages';

/*
  Les seuls formats qu'un navigateur affiche partout. Le HEIC d'un iPhone
  passait `image/*`, s'enregistrait, puis ne s'affichait nulle part.
*/
export const TYPES_D_IMAGE_ACCEPTES: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

export const ACCEPT_IMAGE = Object.keys(TYPES_D_IMAGE_ACCEPTES).join(',');

/*
  Le plafond vaut pour le fichier ENVOYE, donc apres compression : une photo
  de telephone de 8 Mo en ressort sous 400 Ko et doit passer. Le plafond sur
  le fichier choisi ne sert qu'a ne pas faire decoder au navigateur une image
  demesuree.
*/
const TAILLE_MAX_ENVOYEE = 5 * 1024 * 1024;
const TAILLE_MAX_CHOISIE = 25 * 1024 * 1024;

export class ImageRefusee extends Error {}

/** Verifie puis compresse la photo choisie. Leve `ImageRefusee`, message pret a afficher. */
export async function preparerImage(file: File): Promise<File> {
  if (!TYPES_D_IMAGE_ACCEPTES[file.type]) {
    throw new ImageRefusee('Format non accepté. Choisissez une image JPG, PNG ou WebP.');
  }
  if (file.size > TAILLE_MAX_CHOISIE) {
    throw new ImageRefusee('Cette image est trop lourde (plus de 25 Mo).');
  }

  let optimisee: File;
  try {
    optimisee = await optimizeImageIfNeeded(file, 800);
  } catch (error) {
    console.error("Erreur lors de l'optimisation de l'image:", error);
    throw new ImageRefusee('Impossible de traiter cette image. Essayez avec une autre.');
  }

  if (optimisee.size > TAILLE_MAX_ENVOYEE) {
    throw new ImageRefusee('Même compressée, cette image dépasse 5 Mo. Choisissez une image plus légère.');
  }
  return optimisee;
}

/*
  Un nom neuf a chaque envoi. Avec `<id>.png` ecrase sur place, l'adresse ne
  changeait jamais : le cache du navigateur et celui du CDN Supabase (une
  heure) continuaient de servir l'ancienne photo, et l'envoi semblait rate
  alors qu'il avait reussi.
*/
export async function envoyerImage(stageId: string, file: File): Promise<string> {
  const extension = TYPES_D_IMAGE_ACCEPTES[file.type] ?? 'jpg';
  const chemin = `${stageId}-${Date.now()}.${extension}`;

  const { error } = await supabase.storage
    .from(SEAU)
    .upload(chemin, file, { contentType: file.type, upsert: false });
  if (error) throw error;

  return supabase.storage.from(SEAU).getPublicUrl(chemin).data.publicUrl;
}

/*
  Au mieux, sans attendre ni lever : un fichier qui resterait ne casse rien,
  il occupe seulement de la place. Seuls les fichiers du seau `stages` sont
  concernes ; une adresse d'ailleurs est laissee telle quelle.

  A n'appeler que quand plus rien ne renvoie a l'adresse. L'ancienne photo
  d'un stage MODIFIE ne s'y prete pas : les pages publiques sont prerendues
  au deploiement (scripts/prerendre.mjs) et figent son adresse dans og:image
  et le JSON-LD.
*/
export function supprimerImage(url: string): void {
  const chemin = url.split(`/object/public/${SEAU}/`)[1]?.split('?')[0];
  if (!chemin) return;
  supabase.storage
    .from(SEAU)
    .remove([decodeURIComponent(chemin)])
    .then(({ error }) => {
      if (error) console.error('Error removing image:', error);
    });
}
